import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Sentry } from '../lib/sentry';
import { isChunkLoadError, useAppUpdateStore } from '../lib/appUpdate';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    /* ⭐⭐ A CHUNK THAT FAILED TO LOAD IS A DEPLOY, NOT A CRASH (owner, 2026-10-04). The
       tab is running a build whose files were replaced; the update overlay
       (`AppUpdatePrompt`, mounted beside this boundary in main.tsx) takes over. Not
       reported to Sentry — it is expected after every release. */
    if (isChunkLoadError(error)) {
      useAppUpdateStore.getState().markRequired();
      return;
    }
    console.error('Error caught by boundary:', error, errorInfo);
    // Report to Sentry (no-op if Sentry wasn't initialized / no DSN set).
    Sentry.withScope((scope) => {
      scope.setExtras({ componentStack: errorInfo.componentStack });
      Sentry.captureException(error);
    });
    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.reload();
  };

  handleClearCache = () => {
    if (window.confirm('This will clear all local data and reload the app. Continue?')) {
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError && isChunkLoadError(this.state.error)) {
      // ⚠️ Only a backdrop: the overlay itself is AppUpdatePrompt, so it is never shown twice.
      return <div className="min-h-screen bg-background" data-testid="chunk-error-backdrop" />;
    }
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-card border border-border rounded-lg p-8 shadow-lg">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
            </div>

            <p className="text-muted-foreground mb-6">
              The app encountered an unexpected error. Don't worry, your data is safe and stored locally.
            </p>

            <div className="bg-muted rounded-lg p-4 mb-6 max-h-40 overflow-auto">
              <p className="text-sm font-mono text-foreground break-words">
                {this.state.error?.message || 'Unknown error'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
              >
                <RefreshCw className="w-4 h-4" />
                Reload App
              </button>

              <button
                onClick={this.handleClearCache}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:opacity-90 transition-opacity"
              >
                Clear Cache & Reload
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-border">
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Technical Details
                </summary>
                <pre className="mt-4 p-4 bg-muted rounded-lg text-xs overflow-auto max-h-60">
                  {this.state.errorInfo?.componentStack || 'No additional details available'}
                </pre>
              </details>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
